import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { site as siteApi } from "../../services/site";
import CommonModal from "../commonModel/CommonModel";
import CommonButton from "../utilities/formElements/commonButton/CommonButton";

/*
 * Unwraps the JSON-RPC envelope - http.ts returns withData(res.data) =
 * { error, data }, where `data` is { jsonrpc, id, result }. Reading res.data
 * yields the ENVELOPE, not the payload. Getting this wrong in AddSiteModal
 * once reported a real success as a failure.
 */
const rpcResult = (res: any): any =>
  res?.data?.result ?? res?.result ?? res?.data;

/*
 * Removing a website.
 *
 * 🔴 THE CONFIRMATION IS PROPORTIONAL TO WHAT IS LOST, which is the whole
 * design. Most removals are a typo with no data and deserve one click; a site
 * with real history makes you type its host. A single scary dialog for both
 * trains people to click through the one that actually matters.
 *
 * 🔴 IT IS A HARD DELETE, and the copy says so plainly rather than hedging.
 * See SiteApi/client.ts::removeSite for why archive was rejected.
 */
const RemoveSiteModal = ({
  site,
  userId,
  onClose,
  onRemoved,
}: {
  site: any;
  userId: string;
  onClose: () => void;
  onRemoved: (host: string) => void;
}) => {
  const [info, setInfo] = useState<any>(null);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // What is actually at stake. Asked for rather than assumed, so the dialog
  // can state a real number instead of a vague warning.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const res: any = await siteApi({
          jsonprc: "2.0",
          method: "getSiteRemovalInfo",
          params: { userId, siteId: site?._id },
          id: 0,
        } as any);
        const result = rpcResult(res);
        if (!cancelled) {
          setInfo(result ?? { host: site?.host, sessionCount: 0 });
        }
      } catch (e) {
        if (!cancelled) {
          setError("Could not check this website. Please try again.");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [site, userId]);

  const host = info?.host ?? site?.host ?? "";
  const count = info?.sessionCount ?? 0;
  // Only the data-bearing case demands typing. `info === null` means the check
  // has not returned, so the button stays disabled either way.
  const needsTyping = count > 0;
  const confirmed = needsTyping ? typed.trim() === host : true;

  const remove = async () => {
    if (busy || !confirmed || !info) {
      return;
    }

    setBusy(true);
    setError(null);

    try {
      const res: any = await siteApi({
        jsonprc: "2.0",
        method: "removeSite",
        params: { userId, siteId: site?._id },
        id: 0,
      } as any);

      const result = rpcResult(res);

      if (!result?.removed) {
        // The server's own wording is shown verbatim - it already says the
        // useful thing ("You cannot remove your only website.").
        setError(
          res?.error?.message ??
            res?.data?.error?.message ??
            "Could not remove that website.",
        );
        setBusy(false);
        return;
      }

      onRemoved(result.host ?? host);
    } catch (e: any) {
      setError(e?.message ?? "Could not remove that website.");
      setBusy(false);
    }
  };

  return createPortal(
    <CommonModal className="add-site-modal" title="Remove website" onClose={onClose}>
      <p className="add-site-lead">
        {info === null ? (
          <>Checking {site?.host}...</>
        ) : count > 0 ? (
          <>
            This removes <strong>{host}</strong> and its{" "}
            <strong>{count.toLocaleString()}</strong>{" "}
            {count === 1 ? "session" : "sessions"} from your account, along
            with its page slots and goals. You will not be able to undo this
            yourself.
          </>
        ) : (
          <>
            Remove <strong>{host}</strong>? It has no visitor data yet.
          </>
        )}
      </p>

      {needsTyping && (
        <form className="add-site-form" onSubmit={(e) => e.preventDefault()}>
          <input
            type="text"
            autoFocus
            value={typed}
            placeholder={host}
            disabled={busy}
            onChange={(e) => setTyped(e.target.value)}
            aria-label={`Type ${host} to confirm`}
          />
        </form>
      )}

      {needsTyping && (
        <p className="add-site-lead remove-site-hint">
          Type the website name to confirm.
        </p>
      )}

      {/*
        🔴 THIS SENTENCE IS ONLY TRUE BECAUSE THE SERVER ENFORCES IT.
        The handshake now refuses a siteId with no site
        (socket.controller.ts), and removeSite invalidates the site cache so
        the refusal takes effect at once. Before that, a leftover snippet kept
        connecting and kept creating real sessions - measured. If either of
        those changes is ever reverted, this line becomes a lie and must
        change back to asking the customer to remove the snippet first.
      */}
      <p className="add-site-lead remove-site-hint">
        Tracking stops immediately. You can remove the Skydive snippet from{" "}
        {host} whenever it suits you - until you do, visits to it are simply
        ignored.
      </p>

      {error && <p className="add-site-error">{error}</p>}

      {/* 🔴 `button-align-modal` ONLY - `flex-align-modal-btn` is
          `flex-direction: column` despite its name. `mt-4 mb-3` is the house
          spacing (see ChangePassword.tsx). */}
      <div className="button-align-modal mt-4 mb-3">
        <CommonButton
          type="button"
          label="Cancel"
          className="theme-btn success-outline-btn transparent text-uppercase fontBold"
          onClick={onClose}
        />
        <CommonButton
          type="button"
          label={busy ? "Removing..." : "Remove website"}
          className="theme-btn theme-danger-btn text-uppercase fontBold"
          disabled={busy || !confirmed || !info}
          loading={busy}
          onClick={remove}
        />
      </div>
    </CommonModal>,
    document.body,
  );
};

export default RemoveSiteModal;
